import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';

import { getProduceReceiptPrices } from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle } from '../../../shared/components';
import { ERROR_MSG } from '../../../shared/lib/Localization';

export type PriceBasis = 'fixed' | 'calculated' | 'none';

export type LowestSupplier =
  | { kind: 'sole' }
  | { kind: 'one'; supplierId: string }
  | { kind: 'tie'; supplierIds: string[] };

export type SupplierComparison = {
  looseProductNumber: string;
  suppliers: Array<{
    supplierId: string;
    supplierName: string;
    invoiceDescription: string;
    caseQuantity: number;
    caseCost: number;
    invoiceDate: string;
    invoiceNumber: string;
    unitCost: number;
  }>;
  lowest: LowestSupplier;
};

export type CaseSkuPrice = {
  productNumber: string;
  caseQuantity: number;
  caseCost: number | null;
  caseRecommendation: number | null;
  casePriceBasis: PriceBasis;
  koronaPrice: number | null;
  koronaMatches: boolean;
};

export type LooseLinePrice = {
  looseProductNumber: string;
  looseProductName: string;
  costStatus: 'current' | 'old';
  unitCost: number | null;
  effectiveCost: number | null;
  marginPercent: number | null;
  marginSource: 'supplier' | 'commodity-group' | null;
  looseRecommendation: number | null;
  loosePriceBasis: PriceBasis;
  koronaPrice: number | null;
  koronaMatches: boolean;
  tvPrice: number | null;
  tvDone: boolean;
  webPrice: number | null;
  webDone: boolean;
  caseSkus: CaseSkuPrice[];
};

export type ProduceReceiptPrices = {
  stockReceiptId: string;
  supplierComparison: SupplierComparison[];
  lines: LooseLinePrice[];
};

interface ProducePricesDetailUserData {
  selectedBusiness: { business_id: string };
}

interface ProducePricesDetailProps extends RouteComponentProps<{ id: string }> {
  userData: ProducePricesDetailUserData;
}

interface ProducePricesDetailState {
  userLocalData: ProducePricesDetailUserData;
}

const ERROR_STATUSES = [401, 403, 500];

const OLD_COST_LABEL = 'Old';
const FIXED_PRICE_LABEL = 'Fixed';

const formatMoney = (amount: number | null): string => (amount === null ? '' : amount.toFixed(2));

const formatPercent = (percent: number | null): string => (percent === null ? '' : `${percent}%`);

export const ProducePricesDetailScreen = (props: ProducePricesDetailProps) => {
  const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
  const [receiptPrices, setReceiptPrices] = useState<ProduceReceiptPrices | null>(null);
  const srUuid = props.match.params.id;
  const businessId = props.userData && props.userData.selectedBusiness && props.userData.selectedBusiness.business_id;

  useEffect(() => {
    if (!businessId || !srUuid) {
      setReceiptPrices(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(getProduceReceiptPrices(srUuid, businessId))
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((data: ProduceReceiptPrices) => {
        if (cancelled) {
          return;
        }
        setReceiptPrices(data);
        setErrorStatusVisible(false);
      })
      .catch((error: { status?: number } | null) => {
        if (cancelled) {
          return;
        }
        setErrorStatusVisible(!!(error && error.status !== undefined && ERROR_STATUSES.includes(error.status)));
      });
    return () => {
      cancelled = true;
    };
  }, [srUuid, businessId]);

  return (
    <div>
      <ErrorStatusBar isVisible={isErrorStatusVisible} />
      <CustomTableTitle
        history={props.history}
        title={'Produce prices'}
        icon={SVG_ICONS.reports}
      />
      {isErrorStatusVisible ? <Typography variant="body2">{ERROR_MSG}</Typography> : null}
      {receiptPrices ? (
        <Table size="small" aria-label="Produce prices">
          <TableHead>
            <TableRow>
              <TableCell>Product number</TableCell>
              <TableCell>Product name</TableCell>
              <TableCell align="right">Unit cost</TableCell>
              <TableCell align="right">Effective cost</TableCell>
              <TableCell align="right">Margin %</TableCell>
              <TableCell align="right">Loose recommendation</TableCell>
              <TableCell align="right">Case recommendation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(receiptPrices.lines || []).map((line) => (
              <React.Fragment key={line.looseProductNumber}>
                <TableRow>
                  <TableCell>{line.looseProductNumber}</TableCell>
                  <TableCell>{line.looseProductName}</TableCell>
                  <TableCell align="right">
                    {formatMoney(line.unitCost)}
                    {line.costStatus === 'old' ? ` ${OLD_COST_LABEL}` : null}
                  </TableCell>
                  <TableCell align="right">{formatMoney(line.effectiveCost)}</TableCell>
                  <TableCell align="right">{formatPercent(line.marginPercent)}</TableCell>
                  {/* Trailing space keeps this row's last amount apart from the next row's product number in copied text. */}
                  <TableCell align="right">
                    {formatMoney(line.looseRecommendation)}
                    {line.loosePriceBasis === 'fixed' ? ` ${FIXED_PRICE_LABEL}` : null}
                    {' '}
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
                {(line.caseSkus || []).map((caseSku) => (
                  <TableRow key={caseSku.productNumber}>
                    <TableCell>{caseSku.productNumber}</TableCell>
                    <TableCell />
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right">
                      {formatMoney(caseSku.caseRecommendation)}
                      {' '}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: ProducePricesDetailState) => {
  return {
    userData: state.userLocalData,
  };
};

export default connect(mapStateToProps, null)(ProducePricesDetailScreen);
